import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Experiment, ExperimentClient, ExperimentUser } from '@amplitude/experiment-js-client';
import Amplitude from 'amplitude-js';
import { useTimeout } from 'hooks/useTimeout';
import { AmplitudeFlagType, DefaultFlagValues } from 'types/amplitudeFlags';

import Spinner from 'components/Common/Progress/Spinner';

import { AmplitudeExperimentContext, AmplitudeExperimentContextValue } from './ExperimentContext';
import SegmentExposureTrackingProvider from './SegmentExposureTrackingProvider';

export interface AmplitudeExperimentProviderProps {
  proId?: string;
  email?: string;
  organizationUuid?: string;
  orgCreatedAt?: string;
}

const DEFAULT_EXPERIMENT_TRUTHY_VALUE = 'control';

const AmplitudeExperimentProvider: FC<AmplitudeExperimentProviderProps> = ({
  children,
  proId,
  email,
  organizationUuid,
  orgCreatedAt,
}) => {
  const isDevelopment = process.env.NODE_ENV !== 'production';
  const [isInitialized, setIsInitialized] = useState(false);
  const timeoutDone = useTimeout(500);

  const startExperiment = useCallback(
    async (experiment: ExperimentClient) => {
      if (!proId || !email || !organizationUuid || !orgCreatedAt) {
        return;
      }

      const experimentUser: ExperimentUser = {
        user_id: proId,
        user_properties: {
          premium: true,
          pro_uuid: proId,
          org_uuid: organizationUuid,
          org_created_at: orgCreatedAt,
          email,
        },
      };

      await experiment
        .fetch(experimentUser)
        .then((client) => {
          // method fetch always return client in promise resolve even when request is blocked or fails
          return client;
        })
        .finally(() => {
          setIsInitialized(true);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [proId]
  );

  useEffect(
    function handleLongRunningFetch() {
      if (timeoutDone && !isInitialized) {
        setIsInitialized(true);
      }
    },
    [isInitialized, timeoutDone]
  );

  const experiment = useMemo<ExperimentClient | undefined>(() => {
    if (!proId) {
      return undefined;
    }
    const { AMPLITUDE_EXPERIMENT_JS_KEY = '' } = window;

    const amplitude = Amplitude.getInstance();
    amplitude.setUserId(proId.toString());

    const internExperiment = Experiment.initialize(AMPLITUDE_EXPERIMENT_JS_KEY, {
      exposureTrackingProvider: new SegmentExposureTrackingProvider(),
      debug: isDevelopment,
      initialVariants: DefaultFlagValues,
      fetchTimeoutMillis: 500,
    });

    startExperiment(internExperiment);

    return internExperiment;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proId, startExperiment]);

  const isVariantActive = (variantId: AmplitudeFlagType, truthyExperimentValue?: string) => {
    if (!proId || !experiment) {
      return false;
    }
    const variantObject = experiment.variant(variantId);

    if (truthyExperimentValue) {
      return variantObject.value === truthyExperimentValue;
    }

    return variantObject.value !== DEFAULT_EXPERIMENT_TRUTHY_VALUE;
  };

  const getVariantValue = (variantId: AmplitudeFlagType): string | null => {
    if (!proId || !experiment) {
      return null;
    }
    const variantObject = experiment.variant(variantId);
    const { value } = variantObject;

    return value ?? null;
  };

  const getPayloadVariant = <T,>(variantId: AmplitudeFlagType): T | null => {
    if (!proId || !experiment) {
      return null;
    }
    const variantObject = experiment.variant(variantId);

    return variantObject.payload as T;
  };

  const value: AmplitudeExperimentContextValue = {
    isVariantActive,
    getPayloadVariant,
    getVariantValue,
  };

  // Avoid children re-render if state provider is changed.
  return useMemo(
    () =>
      isInitialized ? (
        <AmplitudeExperimentContext.Provider value={value}>{children}</AmplitudeExperimentContext.Provider>
      ) : (
        <Spinner />
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [children, proId, isInitialized]
  );
};

export { AmplitudeExperimentContext, AmplitudeExperimentProvider };
